import { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTrigger,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Form,
  DialogDescription,
} from '@predoc/cow';
import { Plus, AlertCircle } from 'lucide-react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { createProfileSchema, CreateProfileFormData } from '../types';
import { ProfileFormFields } from './ProfileFormFields';
import { Permission, useHasPermission } from '@/utils/roles';
import { useGetOrganizationFacesheets } from '../hooks/facesheetsAPI';
import { useGetOrganizationInfo } from '../hooks/organizationAPI';
import { useCreateProfile } from '../hooks/profilesAPI';
import { toast } from 'sonner';

export const CreateProfileModal = () => {
  const [open, setOpen] = useState(false);
  const { hasPermission } = useHasPermission(Permission.CREATE_PROFILES);

  // Get organization facesheets and info for validation
  const { data: organizationFacesheets } = useGetOrganizationFacesheets({
    orgWideOnly: true,
  });
  const { data: organizationInfo } = useGetOrganizationInfo();
  const createProfile = useCreateProfile();

  // Create schema with organization facesheets and info for validation
  const profileSchema = useMemo(
    () =>
      createProfileSchema(
        organizationFacesheets?.facesheets || [],
        organizationInfo,
        undefined, // No profile available methods for create flow
        undefined, // No profile facesheets for create flow
      ),
    [organizationFacesheets?.facesheets, organizationInfo],
  );

  const form = useForm<CreateProfileFormData>({
    resolver: zodResolver(profileSchema),
    mode: 'onSubmit',
    defaultValues: {
      displayName: '',
      description: '',
      avatarType: 'DEFAULT_RED',
      defaultRetrievalMethods: [],
      defaultFacesheetConfigIds: [],
      expeditedFacesheetConfigIds: [],
    },
    // Only show errors after submission attempt
    reValidateMode: 'onSubmit',
  });

  const handleModalToggle = () => {
    form.reset();
    setOpen((open) => !open);
  };

  const onSubmit: SubmitHandler<CreateProfileFormData> = (data) => {
    createProfile.mutate(
      {
        displayName: data.displayName,
        description: data.description,
        avatarType: data.avatarType,
        defaultRetrievalMethods: data.defaultRetrievalMethods,
        defaultFacesheetConfigIds: data.defaultFacesheetConfigIds,
        expeditedFacesheetConfigIds: data.expeditedFacesheetConfigIds,
      },
      {
        onSuccess: () => {
          toast.success('Profile has been created.');
        },
        onError: () => {
          toast.error('Profile could not be created.');
        },
      },
    );
    handleModalToggle();
  };

  if (!hasPermission) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={handleModalToggle}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <Plus className="size-4" />
          New
        </Button>
      </DialogTrigger>
      <DialogContent className="mx-auto !max-w-[946px]">
        <DialogHeader className="border-b pb-4">
          <DialogTitle className="text-xl font-semibold">
            New Profile
          </DialogTitle>
          <DialogDescription className="sr-only">
            Create a new profile and configure its settings
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <ProfileFormFields form={form} profileId={''} />
            <DialogFooter className="mt-6 border-t pt-4">
              <div className="flex w-full items-center justify-end gap-4">
                {form.formState.isSubmitted && !form.formState.isValid ? (
                  <div className="text-destructive flex items-center gap-2 text-sm">
                    <AlertCircle className="size-4" />
                    Please fix validation errors.
                  </div>
                ) : null}
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleModalToggle}
                  >
                    Cancel
                  </Button>
                  <Button type="submit">Create</Button>
                </div>
              </div>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
